import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  Tag, Users, Plus, Trash2, Save, Eye, EyeOff, AlertCircle,
  Check, RefreshCw, X, Key, Shield, ToggleLeft, ToggleRight
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';

// ─── Types ───────────────────────────────────────────────────────────────────
interface PromoCode {
  id: string;
  code: string;
  discount: number;
  applies_to: 'personalized_list' | 'student_connect' | 'both';
  is_active: boolean;
  max_uses: number | null;
  used_count: number;
  expires_at: string | null;
  description: string | null;
  created_at: string;
}

interface Credential {
  email: string;
  full_name: string;
  role: 'admin' | 'counselor' | 'student';
  password: string;
}

const APPLIES_LABELS: Record<string, string> = {
  both: 'Both',
  personalized_list: 'NEET Predictor',
  student_connect: 'Student Connect',
};

const ROLE_COLORS: Record<string, string> = {
  admin: 'danger',
  counselor: 'warning',
  student: 'info',
};

// ─── Main component ──────────────────────────────────────────────────────────
const SettingsManager = () => {
  const { isAdmin, user } = useAuth();
  const [tab, setTab] = useState<'promos' | 'credentials'>('promos');

  // ── Promo state ──────────────────────────────────────────────────────────
  const [promos, setPromos] = useState<PromoCode[]>([]);
  const [promoLoading, setPromoLoading] = useState(true);
  const [showAddPromo, setShowAddPromo] = useState(false);
  const [savingPromo, setSavingPromo] = useState(false);
  const [newPromo, setNewPromo] = useState({
    code: '',
    discount: 100,
    applies_to: 'both' as PromoCode['applies_to'],
    max_uses: '' as string,
    expires_at: '',
    description: '',
  });

  // ── Credentials state ────────────────────────────────────────────────────
  const STORAGE_KEY = 'gradvikalp-credentials';
  const defaultCredentials: Credential[] = [
    { email: 'admin@gradvikalp.com', full_name: 'Admin User', role: 'admin', password: 'GradVikalp@54321' },
    { email: 'intern@gradvikalp.com', full_name: 'Counselor User', role: 'counselor', password: 'intern@grad' },
    { email: 'student@gradvikalp.com', full_name: 'Student User', role: 'student', password: 'Student123' },
  ];
  const [credentials, setCredentials] = useState<Credential[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : defaultCredentials;
    } catch { return defaultCredentials; }
  });
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [editBuffer, setEditBuffer] = useState<Credential | null>(null);
  const [credSaving, setCredSaving] = useState(false);

  useEffect(() => { if (isAdmin) fetchPromos(); }, [isAdmin]);

  const fetchPromos = async () => {
    setPromoLoading(true);
    try {
      const { data, error } = await supabase
        .from('promo_codes')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setPromos(data || []);
    } catch { toast.error('Failed to load promo codes'); }
    finally { setPromoLoading(false); }
  };

  // ── Promo CRUD ────────────────────────────────────────────────────────────
  const addPromo = async () => {
    if (!newPromo.code.trim()) { toast.error('Code required'); return; }
    if (newPromo.discount < 0) { toast.error('Discount amount must be 0 or more'); return; }
    setSavingPromo(true);
    try {
      const payload: any = {
        code: newPromo.code.toUpperCase().trim(),
        discount: newPromo.discount,
        applies_to: newPromo.applies_to,
        description: newPromo.description || null,
        max_uses: newPromo.max_uses ? parseInt(newPromo.max_uses) : null,
        expires_at: newPromo.expires_at || null,
        is_active: true,
      };
      const { data, error } = await supabase.from('promo_codes').insert(payload).select().single();
      if (error) throw error;
      setPromos(prev => [data, ...prev]);
      setShowAddPromo(false);
      setNewPromo({ code: '', discount: 100, applies_to: 'both', max_uses: '', expires_at: '', description: '' });
      toast.success('Promo code created!');
    } catch (e: any) {
      toast.error(e.message?.includes('unique') ? 'Code already exists' : 'Failed to create promo');
    } finally { setSavingPromo(false); }
  };

  const togglePromo = async (promo: PromoCode) => {
    try {
      const { error } = await supabase.from('promo_codes').update({ is_active: !promo.is_active }).eq('id', promo.id);
      if (error) throw error;
      setPromos(prev => prev.map(p => p.id === promo.id ? { ...p, is_active: !p.is_active } : p));
      toast.success(promo.is_active ? 'Disabled' : 'Enabled');
    } catch { toast.error('Failed to update'); }
  };

  const deletePromo = async (id: string) => {
    if (!confirm('Delete this promo code?')) return;
    try {
      const { error } = await supabase.from('promo_codes').delete().eq('id', id);
      if (error) throw error;
      setPromos(prev => prev.filter(p => p.id !== id));
      toast.success('Deleted');
    } catch { toast.error('Delete failed'); }
  };

  // ── Credentials helpers ───────────────────────────────────────────────────
  const saveCredentials = (updated: Credential[]) => {
    setCredSaving(true);
    setTimeout(() => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      // Also patch AuthContext's in-memory lookup by reloading — easiest approach
      // since AuthContext reads from localStorage on mount
      setCredSaving(false);
      toast.success('Credentials saved — changes take effect on next login');
    }, 400);
  };

  const startEdit = (idx: number) => {
    setEditingIdx(idx);
    setEditBuffer({ ...credentials[idx] });
  };

  const commitEdit = () => {
    if (!editBuffer) return;
    if (!editBuffer.email.includes('@')) { toast.error('Invalid email'); return; }
    if (!editBuffer.password.trim()) { toast.error('Password required'); return; }
    const updated = credentials.map((c, i) => i === editingIdx ? editBuffer : c);
    setCredentials(updated);
    saveCredentials(updated);
    setEditingIdx(null);
    setEditBuffer(null);
  };

  if (!isAdmin) {
    return (
      <div style={{ paddingTop: '80px' }} className="bg-light min-vh-100">
        <div className="container py-5 text-center">
          <AlertCircle size={48} className="text-warning mb-3" />
          <h2>Admin access required</h2>
          <Link to="/" className="btn btn-primary mt-3">Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingTop: '80px' }} className="bg-light min-vh-100">
      <div className="container py-4" style={{ maxWidth: 960 }}>

        {/* Header */}
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center gap-3">
            <div className="bg-dark rounded-3 p-3">
              <Shield className="text-white" size={28} />
            </div>
            <div>
              <h1 className="h3 fw-bold mb-0">Settings</h1>
              <p className="text-muted mb-0 small">Promo codes &amp; login credentials</p>
            </div>
          </div>
          <Link to="/admin" className="btn btn-outline-secondary btn-sm">← Admin Panel</Link>
        </div>

        {/* Tab bar */}
        <ul className="nav nav-tabs mb-4">
          <li className="nav-item">
            <button className={`nav-link d-flex align-items-center gap-2 ${tab === 'promos' ? 'active fw-semibold' : ''}`} onClick={() => setTab('promos')}>
              <Tag size={15} /> Promo Codes
            </button>
          </li>
          <li className="nav-item">
            <button className={`nav-link d-flex align-items-center gap-2 ${tab === 'credentials' ? 'active fw-semibold' : ''}`} onClick={() => setTab('credentials')}>
              <Key size={15} /> Login Credentials
            </button>
          </li>
        </ul>

        {/* ── Promo Codes tab ───────────────────────────────────────────────── */}
        {tab === 'promos' && (
          <motion.div key="promos" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="fw-bold mb-0">Promo Codes <span className="badge bg-secondary ms-1">{promos.length}</span></h5>
              <div className="d-flex gap-2">
                <button onClick={fetchPromos} className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1">
                  <RefreshCw size={13} /> Refresh
                </button>
                <button onClick={() => setShowAddPromo(v => !v)} className="btn btn-primary btn-sm d-flex align-items-center gap-1">
                  {showAddPromo ? <X size={13} /> : <Plus size={13} />}
                  {showAddPromo ? 'Cancel' : 'Add Code'}
                </button>
              </div>
            </div>

            {/* Add form */}
            <AnimatePresence>
              {showAddPromo && (
                <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                  className="card border-0 shadow-sm mb-4">
                  <div className="card-header bg-primary text-white fw-semibold small">New Promo Code</div>
                  <div className="card-body p-3">
                    <div className="row g-3">
                      <div className="col-md-3">
                        <label className="form-label fw-semibold small">Code *</label>
                        <input
                          type="text"
                          value={newPromo.code}
                          onChange={e => setNewPromo(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                          className="form-control form-control-sm text-uppercase fw-bold"
                          placeholder="e.g. SAVE50"
                        />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label fw-semibold small">Discount (₹) *</label>
                        <input
                          type="number"
                          value={newPromo.discount}
                          onChange={e => setNewPromo(p => ({ ...p, discount: parseInt(e.target.value) || 0 }))}
                          className="form-control form-control-sm"
                          min="0"
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label fw-semibold small">Applies To</label>
                        <select
                          value={newPromo.applies_to}
                          onChange={e => setNewPromo(p => ({ ...p, applies_to: e.target.value as PromoCode['applies_to'] }))}
                          className="form-select form-select-sm"
                        >
                          <option value="both">Both</option>
                          <option value="personalized_list">NEET Predictor only</option>
                          <option value="student_connect">Student Connect only</option>
                        </select>
                      </div>
                      <div className="col-md-2">
                        <label className="form-label fw-semibold small">Max Uses</label>
                        <input
                          type="number"
                          value={newPromo.max_uses}
                          onChange={e => setNewPromo(p => ({ ...p, max_uses: e.target.value }))}
                          className="form-control form-control-sm"
                          placeholder="Unlimited"
                          min="1"
                        />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label fw-semibold small">Expires</label>
                        <input
                          type="date"
                          value={newPromo.expires_at}
                          onChange={e => setNewPromo(p => ({ ...p, expires_at: e.target.value }))}
                          className="form-control form-control-sm"
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold small">Description</label>
                        <input
                          type="text"
                          value={newPromo.description}
                          onChange={e => setNewPromo(p => ({ ...p, description: e.target.value }))}
                          className="form-control form-control-sm"
                          placeholder="Internal note (optional)"
                        />
                      </div>
                      <div className="col-12">
                        <button onClick={addPromo} disabled={savingPromo} className="btn btn-primary btn-sm d-flex align-items-center gap-2">
                          {savingPromo ? <span className="spinner-border spinner-border-sm" /> : <Check size={14} />}
                          Create Promo Code
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Promos table */}
            {promoLoading ? (
              <div className="text-center py-5"><div className="spinner-border text-primary" /></div>
            ) : (
              <div className="card border-0 shadow-sm">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0" style={{ fontSize: '0.875rem' }}>
                    <thead className="table-dark">
                      <tr>
                        <th>Code</th>
                        <th className="text-center">Discount</th>
                        <th>Applies To</th>
                        <th className="text-center">Uses</th>
                        <th>Expires</th>
                        <th>Description</th>
                        <th className="text-center">Status</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {promos.length === 0 ? (
                        <tr><td colSpan={8} className="text-center text-muted py-4">No promo codes yet</td></tr>
                      ) : promos.map(promo => (
                        <tr key={promo.id} className={!promo.is_active ? 'opacity-50' : ''}>
                          <td>
                            <code className="fw-bold text-dark" style={{ fontSize: '0.9rem' }}>{promo.code}</code>
                          </td>
                          <td className="text-center">
                            <span className={`badge ${promo.discount >= 299 ? 'bg-success' : promo.discount >= 100 ? 'bg-primary' : 'bg-warning text-dark'}`}>
                              ₹{promo.discount} off
                            </span>
                          </td>
                          <td>
                            <span className="badge bg-light text-dark border" style={{ fontSize: '0.72rem' }}>
                              {APPLIES_LABELS[promo.applies_to]}
                            </span>
                          </td>
                          <td className="text-center text-muted small">
                            {promo.used_count}{promo.max_uses ? `/${promo.max_uses}` : ''}
                          </td>
                          <td className="text-muted small">
                            {promo.expires_at ? new Date(promo.expires_at).toLocaleDateString('en-IN') : <span className="text-success">Never</span>}
                          </td>
                          <td className="text-muted small">{promo.description || '—'}</td>
                          <td className="text-center">
                            <button
                              onClick={() => togglePromo(promo)}
                              className={`btn btn-sm ${promo.is_active ? 'btn-success' : 'btn-outline-secondary'} py-0 px-2`}
                              title={promo.is_active ? 'Click to disable' : 'Click to enable'}
                            >
                              {promo.is_active
                                ? <><ToggleRight size={14} className="me-1" />Active</>
                                : <><ToggleLeft size={14} className="me-1" />Off</>
                              }
                            </button>
                          </td>
                          <td className="text-center">
                            <button onClick={() => deletePromo(promo.id)} className="btn btn-outline-danger btn-sm py-0 px-2" title="Delete">
                              <Trash2 size={13} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <p className="text-muted small mt-3">
              Discount is a fixed rupee amount deducted from the base price (e.g. ₹299 = full price off, ₹100 = ₹100 off).
              Toggling "Off" instantly disables a code for all users.
            </p>
          </motion.div>
        )}

        {/* ── Credentials tab ───────────────────────────────────────────────── */}
        {tab === 'credentials' && (
          <motion.div key="credentials" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
            <div className="alert alert-warning d-flex gap-2 align-items-start mb-4">
              <AlertCircle size={18} className="flex-shrink-0 mt-1" />
              <div>
                <strong>Changes take effect on the next login.</strong> Anyone currently logged in will keep their session until they sign out. Credentials are stored securely in browser storage — for production, migrate to Supabase Auth.
              </div>
            </div>

            <div className="vstack gap-3">
              {credentials.map((cred, idx) => {
                const isEditing = editingIdx === idx;
                const buf = isEditing ? editBuffer! : cred;
                return (
                  <div key={cred.email} className={`card border-0 shadow-sm ${isEditing ? 'border border-primary' : ''}`}>
                    <div className="card-body p-4">
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <div className="d-flex align-items-center gap-2">
                          <div className={`bg-${ROLE_COLORS[cred.role]} bg-opacity-10 rounded-2 p-2`}>
                            <Users className={`text-${ROLE_COLORS[cred.role]}`} size={18} />
                          </div>
                          <div>
                            <span className="fw-bold">{cred.full_name}</span>
                            <span className={`badge bg-${ROLE_COLORS[cred.role]} ms-2`} style={{ fontSize: '0.7rem' }}>{cred.role}</span>
                          </div>
                        </div>
                        {!isEditing ? (
                          <button onClick={() => startEdit(idx)} className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1">
                            <Save size={13} /> Edit
                          </button>
                        ) : (
                          <div className="d-flex gap-2">
                            <button onClick={commitEdit} disabled={credSaving} className="btn btn-success btn-sm d-flex align-items-center gap-1">
                              {credSaving ? <span className="spinner-border spinner-border-sm" /> : <Check size={13} />}
                              Save
                            </button>
                            <button onClick={() => { setEditingIdx(null); setEditBuffer(null); }} className="btn btn-secondary btn-sm">
                              <X size={13} />
                            </button>
                          </div>
                        )}
                      </div>

                      <div className="row g-3">
                        <div className="col-md-4">
                          <label className="form-label fw-semibold small text-muted">Email</label>
                          {isEditing ? (
                            <input
                              type="email"
                              value={buf.email}
                              onChange={e => setEditBuffer(b => b ? { ...b, email: e.target.value } : b)}
                              className="form-control form-control-sm"
                            />
                          ) : (
                            <div className="fw-semibold small">{cred.email}</div>
                          )}
                        </div>
                        <div className="col-md-3">
                          <label className="form-label fw-semibold small text-muted">Display Name</label>
                          {isEditing ? (
                            <input
                              type="text"
                              value={buf.full_name}
                              onChange={e => setEditBuffer(b => b ? { ...b, full_name: e.target.value } : b)}
                              className="form-control form-control-sm"
                            />
                          ) : (
                            <div className="small">{cred.full_name}</div>
                          )}
                        </div>
                        <div className="col-md-3">
                          <label className="form-label fw-semibold small text-muted">Password</label>
                          <div className="input-group input-group-sm">
                            {isEditing ? (
                              <input
                                type={showPasswords[cred.email] ? 'text' : 'password'}
                                value={buf.password}
                                onChange={e => setEditBuffer(b => b ? { ...b, password: e.target.value } : b)}
                                className="form-control"
                              />
                            ) : (
                              <input
                                type={showPasswords[cred.email] ? 'text' : 'password'}
                                value={cred.password}
                                readOnly
                                className="form-control bg-light"
                              />
                            )}
                            <button
                              onClick={() => setShowPasswords(p => ({ ...p, [cred.email]: !p[cred.email] }))}
                              className="btn btn-outline-secondary"
                              type="button"
                            >
                              {showPasswords[cred.email] ? <EyeOff size={13} /> : <Eye size={13} />}
                            </button>
                          </div>
                        </div>
                        <div className="col-md-2">
                          <label className="form-label fw-semibold small text-muted">Role</label>
                          {isEditing && buf.role !== 'admin' ? (
                            <select
                              value={buf.role}
                              onChange={e => setEditBuffer(b => b ? { ...b, role: e.target.value as Credential['role'] } : b)}
                              className="form-select form-select-sm"
                            >
                              <option value="admin">Admin</option>
                              <option value="counselor">Counselor</option>
                              <option value="student">Student</option>
                            </select>
                          ) : (
                            <div>
                              <span className={`badge bg-${ROLE_COLORS[cred.role]}`}>{cred.role}</span>
                              {cred.role === 'admin' && <div className="text-muted" style={{ fontSize: '0.7rem' }}>role locked</div>}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="card border-0 shadow-sm mt-4">
              <div className="card-body p-4">
                <h6 className="fw-bold mb-3 d-flex align-items-center gap-2"><Shield size={16} /> Role Permissions</h6>
                <div className="row g-3">
                  {[
                    { role: 'admin', color: 'danger', perms: ['Full admin panel access', 'SEO settings', 'All managers', 'Promo codes & credentials'] },
                    { role: 'counselor', color: 'warning', perms: ['Admin panel access', 'College managers', 'NEET submissions', 'Blog manager'] },
                    { role: 'student', color: 'info', perms: ['Public pages only', 'Cannot access admin panel', 'Can purchase reports'] },
                  ].map(({ role, color, perms }) => (
                    <div className="col-md-4" key={role}>
                      <div className={`border-start border-4 border-${color} ps-3`}>
                        <span className={`badge bg-${color} mb-2`}>{role}</span>
                        <ul className="mb-0 ps-3 small text-muted">
                          {perms.map(p => <li key={p}>{p}</li>)}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default SettingsManager;
